import React from 'react'
import PropTypes from 'prop-types'
import css from './DataExtractView.css'
import TenantEnabledToggle from "./TenantEnabledToggle"
import copy from "clipboard-copy";
import {pressedEnterOrSpace} from "../utils/accessibility";
import DataExtractAccessibleTabs from "./DataExtractAccessibleTabs";

/**
 * Displays the Data Extracts Tenant Admin page
 *
 * @param {Object} props - see propTypes
 * @returns {JSX.Element} The Data Extracts Tenant Admin page, or a "Loading..." spinner if waiting on API call.
 */
const DataExtractView = (props) => {
    const copyBucketPath = async () => {
        try {
            await copy(props.bucketName);
            props.setToastMessage("Successfully copied bucket path to clipboard.")
        } catch (e) {
            console.warn(e);
            props.setToastMessage("Failed to copy bucket path to clipboard.")
        }
    }

    const getLastExtractDate = () => {
        if (props.lastExtractDate !== "") {
            return <div className={css["text"]}>
                Last extraction at {props.lastExtractDate}
            </div>
        } else {
            return <div className={css["text"]}>
                Not extracted yet
            </div>
        }
    }

    const getBucketPath = () => {
        if (props.bucketName !== "") {
            return <div>
                <div className={css["bold-text"]} id="bucket-path-present">
                    Files located in AWS S3 Bucket:
                </div>
                <div className={css["text"]} id="bucket-path">
                    {props.bucketName}
                </div>
                <a className={css["clipboard-link"]}
                   data-testid="copy-bucket-path"
                   href="#"
                   onClick={copyBucketPath}
                   onKeyDown={(e) => {
                       if (pressedEnterOrSpace(e)) {
                           e.preventDefault();
                           // noinspection JSIgnoredPromiseFromCall
                           copyBucketPath();
                       }
                   }}>
                    Copy to clipboard
                </a>
            </div>
        } else {
            return <div className={css["text"]} id="no-bucket-path">
                Please select files and save before creating an access key.
            </div>
        }
    }

    if (props.getDataSuccess) {
        return (
            <div>
                <p className={css["page-description"]}>
                    Data extracts provide access to the Starfish data in your tenant. Specified user
                    credentials have controlled permissions to access the data using external tools outside
                    Starfish. <a
                    href="https://help.starfishsolutions.com/Starfish/FI3VYgGm6zb3OWi2R2d2/Help/Admin/IRC/Content/Starfish_Help/Integration/DataExtractsOverview.htm">
                    More info in the Help Center.</a>
                </p>
                <TenantEnabledToggle
                    checked={props.tenantEnabled}
                    setTenantEnabled={props.setTenantEnabled}
                    saveTenantEnabledFlag={props.saveTenantEnabledFlag}
                    setSaveTenantEnabledFlagSuccess={props.setSaveTenantEnabledFlagSuccess}/>
                {props.saveTenantEnabledFlagSuccess && props.tenantEnabled &&
                    <div>
                        <div className={css["shaded-block"]} data-testid="manageData">
                            {getBucketPath()}
                            <div className={css["padding"]}>
                                <div className={css["bold-text"]}>Extract Status</div>
                            </div>
                            {getLastExtractDate()}
                        </div>
                        <DataExtractAccessibleTabs selectedTabIndex={props.selectedTabIndex}
                                                   setSelectedTabIndex={props.setSelectedTabIndex}
                                                   reportList={props.reportList}
                                                   onReportChange={props.onReportChange}
                                                   onSelectAllChange={props.onSelectAllChange}
                                                   isOpenNewAccessKey={props.isOpenNewAccessKey}
                                                   setIsOpenNewAccessKey={props.setIsOpenNewAccessKey}
                                                   selectedOption={props.selectedOption}
                                                   setSelectedOption={props.setSelectedOption}
                                                   onNewAccessKeyCancel={props.onNewAccessKeyCancel}
                                                   createUser={props.createUser}
                                                   userCreated={props.userCreated}
                                                   userCredentials={props.userCredentials}
                                                   userList={props.userList}
                                                   setIsOpenDeleteAccessKey={props.setIsOpenDeleteAccessKey}
                                                   setDeleteUserName={props.setDeleteUserName}
                                                   maxAccessKeys={props.maxAccessKeys}
                                                   showSecretKey={props.showSecretKey}
                                                   onClickShowSecretKey={props.onClickShowSecretKey}
                                                   setIsOpenUpdateAccessKeyStatus={props.setIsOpenUpdateAccessKeyStatus}
                                                   onAccessKeyStatusChange={props.onAccessKeyStatusChange}
                                                   setIsOpen={props.setIsOpen}
                                                   saveReports={props.saveReports}/>
                    </div>
                }
                {!props.saveTenantEnabledFlagSuccess &&
                    <div className={css['padding']}>
                        <div className={css["page-description"]}>Processing. Please wait...</div>
                    </div>
                }
            </div>
        )
    } else {
        return <div className={css["page-description"]}>Loading...</div>
    }
}

DataExtractView.propTypes = {
    getDataSuccess: PropTypes.bool.isRequired,
    bucketName: PropTypes.string.isRequired,
    tenantEnabled: PropTypes.bool.isRequired,
    setTenantEnabled: PropTypes.func.isRequired,
    reportList: PropTypes.arrayOf(PropTypes.object),
    onReportChange: PropTypes.func.isRequired,
    onSelectAllChange: PropTypes.func.isRequired,
    lastExtractDate: PropTypes.string.isRequired,
    isOpenNewAccessKey: PropTypes.bool.isRequired,
    setIsOpenNewAccessKey: PropTypes.func.isRequired,
    selectedOption: PropTypes.object,
    setSelectedOption: PropTypes.func.isRequired,
    onNewAccessKeyCancel: PropTypes.func.isRequired,
    createUser: PropTypes.func.isRequired,
    userCreated: PropTypes.bool.isRequired,
    userCredentials: PropTypes.object,
    userList: PropTypes.arrayOf(PropTypes.object),
    setIsOpenDeleteAccessKey: PropTypes.func.isRequired,
    setDeleteUserName: PropTypes.func.isRequired,
    maxAccessKeys: PropTypes.number.isRequired,
    selectedTabIndex: PropTypes.number.isRequired,
    setSelectedTabIndex: PropTypes.func.isRequired,
    showSecretKey: PropTypes.bool.isRequired,
    onClickShowSecretKey: PropTypes.func.isRequired,
    setIsOpenUpdateAccessKeyStatus: PropTypes.func.isRequired,
    onAccessKeyStatusChange: PropTypes.func.isRequired,
    setToastMessage: PropTypes.func.isRequired,
    saveTenantEnabledFlag: PropTypes.func.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    saveTenantEnabledFlagSuccess: PropTypes.bool.isRequired,
    setSaveTenantEnabledFlagSuccess: PropTypes.func.isRequired,
    saveReports: PropTypes.func.isRequired
}

export default DataExtractView
